// @flow
import { type TFunction } from 'react-i18next';

type Data = {
    path: string,
    type: string
};

type Element = {
    data: Data,
    label: string,
    type: string,
    elements: Array<Element>,
    options: $FixMe,
    rule: $FixMe
};

type ProductSettingsListItem = {
    label: string,
    setting: string,
    settingText: string,
    uiPath: Array<string>,
    rule?: $FixMe
};

export type ProductSettingsList = Array<ProductSettingsListItem>;

const toSettingsText = (product: string, path: string, t: TFunction) => {
    const parts = path.split('.');
    const key = `settings.${product}.fields.${parts.join('.fields.')}.label`;
    return t(key);
};

const getUiPathId = (path: string, key: string) => {
    const segments = path.split('.');
    const idx = segments.findIndex(item => item === key);
    return segments[idx + 1];
};

const extractData = (data: Data, uiPath: Array<string>, t: TFunction) => {
    const [product, section, module] = uiPath;

    const productText = t(`settings.products.${product}`);
    const sectionText = t(section);
    const moduleText = module ? t(module) : '';

    // format: "TrackIQ / Via.sh / Header Module"
    let label = `${productText} / ${sectionText}`;

    // Few cases we directly have controls inside Tab's without vertical tab's.
    if (moduleText) {
        label += ` / ${moduleText}`;
    }

    const sectionId = getUiPathId(section, 'sections');
    const moduleId = getUiPathId(module ?? '', 'modules');

    return {
        label,
        uiPath: [product, sectionId, moduleId],
        setting: data.path,
        settingText: toSettingsText(product, data.path, t)
    };
};

const toProductSettingsList = (
    element: Element,
    uiPath: Array<string>,
    isGlobalPersonnel: boolean,
    t: TFunction,
    rule?: $FixMe
): ProductSettingsList => {
    const { data, label, type, elements, rule: ruleToShow } = element;

    const settings: ProductSettingsList = [];
    const newUiPath = [...uiPath];

    // constructing the path of the field as we traverse recursively.
    // eg. TrackIQ - Lotr - Scan Settings.
    if (type === 'Tab') {
        newUiPath.push(label);
    }

    // we only have 'data' for the fields controls. So extracting the info here.
    if (data) {
        const isMaraOnlyField = element.options?.isMaraOnlyField;

        // Check for mara only fields
        const canAddData =
            !isMaraOnlyField || (isMaraOnlyField && isGlobalPersonnel);

        // Extract the information and add it to the result.
        if (canAddData) {
            settings.push({ ...extractData(data, newUiPath, t), rule });
        }
    } else {
        // Temp Hack: added to prevent crashing in few cases where
        // we don't have a 'data' and it's not a layout with elements as array.
        // Will remove this after proper fix.
        if (!Array.isArray(element) && !Array.isArray(elements)) {
            return settings;
        }

        // if element is Layout then it will have 'elements'
        // else if it's list of controls, in that case it's just an array
        (elements ?? element).forEach(e =>
            settings.push(
                ...toProductSettingsList(
                    e,
                    newUiPath,
                    isGlobalPersonnel,
                    t,
                    ruleToShow
                )
            )
        );
    }

    return settings;
};

export default toProductSettingsList;
